/**
 * Roadmap do projeto — calendário com dia, semana, mês e ano.
 * O mês abre no período em que há trabalho. A lista acima mostra só essa janela.
 */

import React, { useMemo, useState } from 'react';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Trash2,
  Flag,
  Wrench,
  GraduationCap,
  ClipboardCheck,
  Eye,
  CheckCircle2,
  Clock,
  Pause,
  XCircle,
} from 'lucide-react';
import { RoadmapItem } from '../../types/consultingProject';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import { useAuth } from '../../context/AuthContext';
import { InvitationService } from '../../services/invitationService';
import { parseRoadmapTeam, serializeRoadmapTeam } from '../../utils/roadmapTeam';

interface ProjectRoadmapProps {
  items: RoadmapItem[];
  organizationId?: string;
  projectId?: string;
  projectName?: string;
  onAddItem: (item: Partial<RoadmapItem>) => void;
  onUpdateItem: (id: string, updates: Partial<RoadmapItem>) => void;
  onDeleteItem: (id: string) => void;
  onClickItem?: (item: RoadmapItem) => void;
  readOnly?: boolean;
}

const EMPTY_FORM = {
  title: '',
  description: '',
  area: '',
  type: 'kaizen' as RoadmapItem['type'],
  plannedStart: '',
  plannedEnd: '',
  userIds: [] as string[],
  pendingEmails: [] as string[],
  legacyNames: [] as string[],
  expectedGains: '',
};

type Scale = 'day' | 'week' | 'month' | 'year';

const TYPE_CONFIG = {
  kaizen: { icon: Wrench, label: 'Melhoria Ativa', color: '#10B981' },
  milestone: { icon: Flag, label: 'Marco', color: '#8B5CF6' },
  training: { icon: GraduationCap, label: 'Treinamento', color: '#3B82F6' },
  audit: { icon: ClipboardCheck, label: 'Auditoria', color: '#F59E0B' },
  review: { icon: Eye, label: 'Revisão', color: '#6366F1' },
};

const STATUS_CONFIG = {
  planned: { icon: Clock, label: 'Planejado', color: '#94A3B8' },
  in_progress: { icon: Clock, label: 'Em Andamento', color: '#3B82F6' },
  completed: { icon: CheckCircle2, label: 'Concluído', color: '#10B981' },
  postponed: { icon: Pause, label: 'Adiado', color: '#F59E0B' },
  cancelled: { icon: XCircle, label: 'Cancelado', color: '#EF4444' },
};

const SCALES: { id: Scale; label: string }[] = [
  { id: 'day', label: 'Dia' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
  { id: 'year', label: 'Ano' },
];

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_FULL = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

interface Column {
  key: string;
  label: string;
  sublabel?: string;
  isToday: boolean;
  isWeekend: boolean;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDay(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(value);
  return startOfDay(parsed);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function startOfWeek(date: Date): Date {
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(startOfDay(date), diff);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatShort(date: Date): string {
  return date.toLocaleDateString('pt-BR');
}

function itemStart(item: RoadmapItem): Date {
  return parseDay(item.plannedStart);
}

function itemEndExclusive(item: RoadmapItem): Date {
  return addDays(parseDay(item.plannedEnd), 1);
}

function overlaps(item: RoadmapItem, rangeStart: Date, rangeEnd: Date): boolean {
  return itemStart(item).getTime() < rangeEnd.getTime() && itemEndExclusive(item).getTime() > rangeStart.getTime();
}

function visibleRange(scale: Scale, cursor: Date): { start: Date; end: Date } {
  if (scale === 'day') {
    const start = startOfDay(cursor);
    return { start, end: addDays(start, 1) };
  }
  if (scale === 'week') {
    const start = startOfWeek(cursor);
    return { start, end: addDays(start, 7) };
  }
  if (scale === 'month') {
    const start = startOfMonth(cursor);
    return { start, end: addMonths(start, 1) };
  }
  const start = new Date(cursor.getFullYear(), 0, 1);
  return { start, end: new Date(cursor.getFullYear() + 1, 0, 1) };
}

function shiftCursor(scale: Scale, cursor: Date, direction: -1 | 1): Date {
  if (scale === 'day') return addDays(cursor, direction);
  if (scale === 'week') return addDays(cursor, direction * 7);
  if (scale === 'month') return addMonths(startOfMonth(cursor), direction);
  return new Date(cursor.getFullYear() + direction, cursor.getMonth(), 1);
}

function periodLabel(scale: Scale, cursor: Date): string {
  if (scale === 'year') return String(cursor.getFullYear());
  if (scale === 'month') {
    const label = `${MONTHS_FULL[cursor.getMonth()]} de ${cursor.getFullYear()}`;
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (scale === 'day') {
    const label = cursor.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  const start = startOfWeek(cursor);
  const end = addDays(start, 6);
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()}–${end.getDate()} de ${MONTHS_FULL[start.getMonth()]} de ${start.getFullYear()}`;
  }
  return `${start.getDate()} ${MONTHS[start.getMonth()]} – ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
}

function columnsFor(scale: Scale, cursor: Date, today: Date): Column[] {
  if (scale === 'week') {
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, index) => {
      const day = addDays(start, index);
      return {
        key: day.toISOString(),
        label: String(day.getDate()),
        sublabel: WEEKDAYS[day.getDay()],
        isToday: sameDay(day, today),
        isWeekend: day.getDay() === 0 || day.getDay() === 6,
      };
    });
  }
  if (scale === 'month') {
    const start = startOfMonth(cursor);
    const days = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    return Array.from({ length: days }, (_, index) => {
      const day = addDays(start, index);
      return {
        key: `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`,
        label: String(day.getDate()),
        sublabel: WEEKDAYS[day.getDay()].slice(0, 1),
        isToday: sameDay(day, today),
        isWeekend: day.getDay() === 0 || day.getDay() === 6,
      };
    });
  }
  return MONTHS.map((month, index) => ({
    key: `${cursor.getFullYear()}-${index}`,
    label: month,
    isToday: today.getFullYear() === cursor.getFullYear() && today.getMonth() === index,
    isWeekend: false,
  }));
}

function initialCursor(items: RoadmapItem[], today: Date): Date {
  const day = startOfDay(today);
  if (items.some(item => overlaps(item, day, addDays(day, 1)))) return day;
  const upcoming = items
    .map(itemStart)
    .filter(date => date.getTime() >= day.getTime())
    .sort((a, b) => a.getTime() - b.getTime())[0];
  if (upcoming) return upcoming;
  const latest = items.map(itemStart).sort((a, b) => b.getTime() - a.getTime())[0];
  return latest ?? day;
}

function packRows(items: RoadmapItem[]): RoadmapItem[][] {
  const sorted = [...items].sort((a, b) => itemStart(a).getTime() - itemStart(b).getTime());
  const rows: { end: number; items: RoadmapItem[] }[] = [];
  sorted.forEach(item => {
    const start = itemStart(item).getTime();
    const end = itemEndExclusive(item).getTime();
    let row = rows.find(entry => entry.end <= start);
    if (!row) {
      row = { end, items: [] };
      rows.push(row);
    }
    row.end = Math.max(row.end, end);
    row.items.push(item);
  });
  return rows.map(row => row.items);
}

function barBox(item: RoadmapItem, rangeStart: Date, rangeEnd: Date) {
  const start = Math.max(itemStart(item).getTime(), rangeStart.getTime());
  const end = Math.min(itemEndExclusive(item).getTime(), rangeEnd.getTime());
  const total = rangeEnd.getTime() - rangeStart.getTime();
  if (end <= start || total <= 0) return null;
  return {
    left: ((start - rangeStart.getTime()) / total) * 100,
    width: ((end - start) / total) * 100,
  };
}

function isLate(item: RoadmapItem, today: Date): boolean {
  if (item.status === 'completed' || item.status === 'cancelled') return false;
  return parseDay(item.plannedEnd).getTime() < startOfDay(today).getTime();
}

export const ProjectRoadmap: React.FC<ProjectRoadmapProps> = ({
  items,
  onAddItem,
  onUpdateItem,
  organizationId,
  projectId,
  projectName,
  onDeleteItem,
  onClickItem,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const { members, loading: membersLoading } = useOrgMembers(organizationId);
  const today = useMemo(() => startOfDay(new Date()), []);
  const [scale, setScale] = useState<Scale>('month');
  const [cursor, setCursor] = useState(() => initialCursor(items, today));
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [contextMenu, setContextMenu] = useState<{ item: RoadmapItem; x: number; y: number } | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [memberQuery, setMemberQuery] = useState('');
  const [inviteDraft, setInviteDraft] = useState('');
  const [inviteNote, setInviteNote] = useState<string | null>(null);

  const range = visibleRange(scale, cursor);
  const columns = scale === 'day' ? [] : columnsFor(scale, cursor, today);
  const periodItems = useMemo(
    () => [...items]
      .filter(item => overlaps(item, range.start, range.end))
      .sort((a, b) => itemStart(a).getTime() - itemStart(b).getTime()),
    [items, range.start, range.end],
  );

  const lanes = useMemo(() => {
    const grouped: Record<string, RoadmapItem[]> = {};
    periodItems.forEach(item => {
      const area = item.area || 'Geral';
      if (!grouped[area]) grouped[area] = [];
      grouped[area].push(item);
    });
    return grouped;
  }, [periodItems]);

  const dayGroups = useMemo(() => {
    const grouped: Record<string, RoadmapItem[]> = {};
    periodItems.forEach(item => {
      const area = item.area || 'Geral';
      if (!grouped[area]) grouped[area] = [];
      grouped[area].push(item);
    });
    return grouped;
  }, [periodItems]);

  const memberName = (userId: string) => members.find(member => member.id === userId)?.name ?? 'Pessoa';

  const teamSummary = (item: RoadmapItem) => {
    const parsed = parseRoadmapTeam(item.team);
    const labels = [
      ...parsed.userIds.map(memberName),
      ...parsed.legacyNames,
      ...parsed.pendingEmails.map(email => `${email} · convite pendente`),
    ];
    return labels.join(', ');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setMemberQuery('');
    setInviteDraft('');
  };

  const toggleMember = (userId: string) => {
    setFormData(current => ({
      ...current,
      userIds: current.userIds.includes(userId)
        ? current.userIds.filter(id => id !== userId)
        : [...current.userIds, userId],
    }));
  };

  const addInviteEmail = () => {
    const email = inviteDraft.trim().toLowerCase();
    if (!email.includes('@')) return;
    const existing = members.find(member => member.email.toLowerCase() === email);
    if (existing) {
      setFormData(current => ({
        ...current,
        userIds: current.userIds.includes(existing.id) ? current.userIds : [...current.userIds, existing.id],
      }));
      setInviteDraft('');
      return;
    }
    setFormData(current => (
      current.pendingEmails.includes(email)
        ? current
        : { ...current, pendingEmails: [...current.pendingEmails, email] }
    ));
    setInviteDraft('');
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.plannedStart || !formData.plannedEnd) return;
    const previousEmails = editingItem ? parseRoadmapTeam(editingItem.team).pendingEmails : [];
    const freshEmails = formData.pendingEmails.filter(email => !previousEmails.includes(email));
    if (organizationId && freshEmails.length > 0) {
      const results = await Promise.all(freshEmails.map(async (email) => {
        try {
          await InvitationService.create({
            organization_id: organizationId,
            email,
            role: 'operator',
            project_id: projectId,
            organizationName: projectName ?? '',
            inviterName: user?.name || user?.email || '',
          });
          return null;
        } catch (error) {
          const message = error instanceof Error ? error.message : '';
          if (message.toLowerCase().includes('pendente')) return null;
          return email;
        }
      }));
      const failed = results.filter((email): email is string => Boolean(email));
      setInviteNote(failed.length > 0
        ? `O evento foi salvo. O convite não saiu para ${failed.join(', ')}.`
        : null);
    } else {
      setInviteNote(null);
    }
    const itemData: Partial<RoadmapItem> = {
      title: formData.title,
      description: formData.description,
      area: formData.area || 'Geral',
      type: formData.type,
      plannedStart: formData.plannedStart,
      plannedEnd: formData.plannedEnd,
      team: serializeRoadmapTeam(formData.userIds, formData.pendingEmails, formData.legacyNames),
      expectedGains: formData.expectedGains,
    };
    if (editingItem) onUpdateItem(editingItem.id, itemData);
    else onAddItem(itemData);
    closeForm();
  };

  const handleEdit = (item: RoadmapItem) => {
    const team = parseRoadmapTeam(item.team);
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      area: item.area,
      type: item.type,
      plannedStart: item.plannedStart.substring(0, 10),
      plannedEnd: item.plannedEnd.substring(0, 10),
      userIds: team.userIds,
      pendingEmails: team.pendingEmails,
      legacyNames: team.legacyNames,
      expectedGains: item.expectedGains || '',
    });
    setShowForm(true);
    setContextMenu(null);
  };

  const openItem = (item: RoadmapItem) => {
    if (onClickItem) onClickItem(item);
    else if (!readOnly) handleEdit(item);
  };

  const barHeight = scale === 'week' ? 44 : 28;
  const barGap = scale === 'week' ? 52 : 34;

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCursor(today)}
          className="rounded border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Hoje
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCursor(current => shiftCursor(scale, current, -1))}
            className="rounded p-1 hover:bg-slate-200 dark:hover:bg-slate-700"
            aria-label="Período anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[220px] text-center text-sm font-semibold text-slate-800 dark:text-slate-100">
            {periodLabel(scale, cursor)}
          </span>
          <button
            type="button"
            onClick={() => setCursor(current => shiftCursor(scale, current, 1))}
            className="rounded p-1 hover:bg-slate-200 dark:hover:bg-slate-700"
            aria-label="Próximo período"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="inline-flex overflow-hidden rounded border border-slate-200 dark:border-slate-700">
          {SCALES.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => setScale(option.id)}
              className={`px-2.5 py-1 text-[11px] font-semibold ${
                scale === option.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          {!readOnly && (
            <button
              type="button"
              onClick={() => { closeForm(); setShowForm(true); setEditingItem(null); }}
              className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            >
              <Plus size={14} />
              Adicionar
            </button>
          )}
        </div>
      </div>

      {inviteNote && (
        <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">{inviteNote}</p>
      )}

      <div className="flex flex-wrap gap-3 text-[11px]">
        {Object.entries(TYPE_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: config.color }} />
            <span className="text-slate-600 dark:text-slate-400">{config.label}</span>
          </div>
        ))}
      </div>

      {scale !== 'day' && (
      <div className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800">
          Neste período · {periodItems.length}
        </div>
        {periodItems.length === 0 ? (
          <p className="px-2 py-2 text-[11px] text-slate-400">Nenhum compromisso neste período.</p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {periodItems.map(item => {
              const late = isLate(item, today);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openItem(item)}
                  className="grid w-full grid-cols-[minmax(0,1.4fr)_110px_90px_150px_90px] items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-800 dark:text-slate-100">{item.title}</span>
                    {teamSummary(item) && (
                      <span className="block truncate text-[10px] text-slate-400">{teamSummary(item)}</span>
                    )}
                  </span>
                  <span className="truncate text-slate-500">{TYPE_CONFIG[item.type].label}</span>
                  <span className="truncate text-slate-500">{item.area || 'Geral'}</span>
                  <span className="font-mono text-slate-500">
                    {formatShort(itemStart(item))} – {formatShort(parseDay(item.plannedEnd))}
                  </span>
                  <span className={`text-right ${late ? 'font-semibold text-red-600' : 'text-slate-500'}`}>
                    {late ? 'Atrasado' : STATUS_CONFIG[item.status].label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {scale === 'day' ? (
        <div className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          {periodItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-slate-400">
              <Calendar size={28} className="mx-auto mb-2 opacity-60" />
              <p className="text-sm">Nenhum compromisso neste dia.</p>
            </div>
          ) : (
            Object.entries(dayGroups).map(([area, areaItems]) => (
              <div key={area} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                <div className="bg-slate-50 px-3 py-1.5 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {area}
                  <span className="ml-2 font-normal text-slate-400">{areaItems.length}</span>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {areaItems.map(item => {
                    const TypeIcon = TYPE_CONFIG[item.type].icon;
                    const late = isLate(item, today);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => openItem(item)}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          if (!readOnly) setContextMenu({ item, x: event.clientX, y: event.clientY });
                        }}
                        className="flex w-full items-start gap-3 px-3 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded" style={{ backgroundColor: TYPE_CONFIG[item.type].color }}>
                          <TypeIcon size={14} className="text-white" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{item.title}</span>
                          <span className="mt-0.5 block text-[11px] text-slate-500">
                            {TYPE_CONFIG[item.type].label} · {item.area || 'Geral'} · {late ? 'Atrasado' : STATUS_CONFIG[item.status].label}
                          </span>
                          <span className="mt-0.5 block font-mono text-[10px] text-slate-400">
                            {formatShort(itemStart(item))} – {formatShort(parseDay(item.plannedEnd))}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div
            className="grid border-b border-slate-200 dark:border-slate-700"
            style={{ gridTemplateColumns: `128px repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            <div className="border-r border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:border-slate-700 dark:bg-slate-800">
              Área
            </div>
            {columns.map(column => (
              <div
                key={column.key}
                className={`border-r border-slate-100 py-1 text-center last:border-r-0 dark:border-slate-800 ${
                  column.isToday
                    ? 'bg-blue-50 font-bold text-blue-600 dark:bg-blue-950/40 dark:text-blue-300'
                    : column.isWeekend
                      ? 'bg-slate-50 text-slate-400 dark:bg-slate-800/60'
                      : 'text-slate-500'
                }`}
              >
                {column.sublabel && (
                  <div className="text-[8px] uppercase leading-none">{column.sublabel}</div>
                )}
                <div className={`text-[10px] leading-tight ${scale === 'week' ? 'text-xs font-semibold' : ''}`}>{column.label}</div>
              </div>
            ))}
          </div>

          {Object.keys(lanes).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Nenhum compromisso neste período.</div>
          ) : (
            Object.entries(lanes).map(([area, laneItems]) => {
              const rows = packRows(laneItems);
              const height = Math.max(scale === 'week' ? 64 : 48, rows.length * barGap + 12);
              return (
                <div
                  key={area}
                  className="grid border-b border-slate-100 last:border-0 dark:border-slate-800"
                  style={{ gridTemplateColumns: `128px minmax(0, 1fr)` }}
                >
                  <div className="border-r border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                    <span className="block truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{area}</span>
                    <span className="text-[10px] text-slate-400">{laneItems.length} {laneItems.length === 1 ? 'item' : 'itens'}</span>
                  </div>
                  <div className="relative" style={{ height }}>
                    <div
                      className="absolute inset-0 grid"
                      style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
                    >
                      {columns.map(column => (
                        <div
                          key={column.key}
                          className={`border-r border-slate-50 last:border-r-0 dark:border-slate-800 ${
                            column.isToday ? 'bg-blue-50/70 dark:bg-blue-950/20' : column.isWeekend ? 'bg-slate-50/80 dark:bg-slate-800/40' : ''
                          }`}
                        />
                      ))}
                    </div>
                    {rows.map((row, rowIndex) => row.map(item => {
                      const box = barBox(item, range.start, range.end);
                      if (!box) return null;
                      const TypeIcon = TYPE_CONFIG[item.type].icon;
                      const typeConfig = TYPE_CONFIG[item.type];
                      const showDetail = scale === 'week' || box.width >= 12;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className="absolute z-10 flex items-center overflow-hidden rounded-md px-1.5 text-left text-white hover:brightness-110"
                          style={{
                            left: `${box.left}%`,
                            width: `${box.width}%`,
                            minWidth: 12,
                            top: rowIndex * barGap + 8,
                            height: barHeight,
                            backgroundColor: item.status === 'cancelled' ? '#94A3B8' : typeConfig.color,
                            opacity: item.status === 'cancelled' ? 0.45 : item.status === 'postponed' ? 0.7 : 1,
                          }}
                          title={`${item.title} · ${TYPE_CONFIG[item.type].label} · ${isLate(item, today) ? 'Atrasado' : STATUS_CONFIG[item.status].label}`}
                          onClick={() => openItem(item)}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            if (!readOnly) setContextMenu({ item, x: event.clientX, y: event.clientY });
                          }}
                        >
                          <TypeIcon size={12} className="shrink-0" />
                          {showDetail && (
                            <span className={`ml-1 min-w-0 ${scale === 'week' ? 'flex flex-col' : 'truncate'}`}>
                              <span className="block truncate text-[11px] font-semibold leading-tight">{item.title}</span>
                              {scale === 'week' && (
                                <span className="block truncate text-[9px] text-white/90">
                                  {typeConfig.label} · {isLate(item, today) ? 'Atrasado' : STATUS_CONFIG[item.status].label}
                                </span>
                              )}
                            </span>
                          )}
                        </button>
                      );
                    }))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {contextMenu && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setContextMenu(null)} />
          <div
            className="fixed z-50 min-w-[160px] rounded border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-800"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              type="button"
              onClick={() => handleEdit(contextMenu.item)}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <Edit3 size={12} /> Editar
            </button>
            {(['planned', 'in_progress', 'completed', 'postponed'] as const).map(status => (
              contextMenu.item.status !== status && (
                <button
                  key={status}
                  type="button"
                  onClick={() => {
                    onUpdateItem(contextMenu.item.id, { status });
                    setContextMenu(null);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {React.createElement(STATUS_CONFIG[status].icon, { size: 12, style: { color: STATUS_CONFIG[status].color } })}
                  {STATUS_CONFIG[status].label}
                </button>
              )
            ))}
            <hr className="my-1 border-slate-200 dark:border-slate-700" />
            <button
              type="button"
              onClick={() => {
                onDeleteItem(contextMenu.item.id);
                setContextMenu(null);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 size={12} /> Excluir
            </button>
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded bg-white p-6 shadow-2xl dark:bg-slate-800">
            <h3 className="mb-4 text-lg font-bold text-slate-800 dark:text-slate-200">
              {editingItem ? 'Editar Item' : 'Novo Item do Roadmap'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={event => setFormData(current => ({ ...current, title: event.target.value }))}
                  className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  placeholder="Ex: Melhoria Layout - Almoxarifado"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={event => setFormData(current => ({ ...current, type: event.target.value as RoadmapItem['type'] }))}
                    className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  >
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Área</label>
                  <input
                    type="text"
                    value={formData.area}
                    onChange={event => setFormData(current => ({ ...current, area: event.target.value }))}
                    className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                    placeholder="Ex: Produção, Logística"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Data Início *</label>
                  <input
                    type="date"
                    value={formData.plannedStart}
                    onChange={event => setFormData(current => ({ ...current, plannedStart: event.target.value }))}
                    className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Data Fim *</label>
                  <input
                    type="date"
                    value={formData.plannedEnd}
                    onChange={event => setFormData(current => ({ ...current, plannedEnd: event.target.value }))}
                    className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={event => setFormData(current => ({ ...current, description: event.target.value }))}
                  className="w-full resize-none rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  rows={2}
                  placeholder="Descrição do item..."
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Pessoas</label>
                <p className="mb-2 text-[11px] text-slate-500">Quem for marcado vê este compromisso na mesa, separado das ações.</p>
                <input
                  type="search"
                  value={memberQuery}
                  onChange={event => setMemberQuery(event.target.value)}
                  className="mb-2 w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  placeholder="Buscar na organização"
                />
                <div className="max-h-36 space-y-1 overflow-auto rounded border border-slate-200 p-1 dark:border-slate-600">
                  {membersLoading && <p className="px-2 py-1 text-[11px] text-slate-400">Carregando pessoas...</p>}
                  {!membersLoading && members.filter(member => {
                    const query = memberQuery.trim().toLowerCase();
                    if (!query) return true;
                    return member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query);
                  }).length === 0 && (
                    <p className="px-2 py-1 text-[11px] text-slate-400">Nenhuma pessoa encontrada.</p>
                  )}
                  {members
                    .filter(member => {
                      const query = memberQuery.trim().toLowerCase();
                      if (!query) return true;
                      return member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query);
                    })
                    .map(member => (
                      <label key={member.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-700">
                        <input
                          type="checkbox"
                          checked={formData.userIds.includes(member.id)}
                          onChange={() => toggleMember(member.id)}
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-slate-700 dark:text-slate-200">{member.name}</span>
                          {member.email && <span className="block truncate text-[10px] text-slate-400">{member.email}</span>}
                        </span>
                      </label>
                    ))}
                </div>
                {(formData.userIds.length > 0 || formData.pendingEmails.length > 0 || formData.legacyNames.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {formData.userIds.map(userId => (
                      <button key={userId} type="button" onClick={() => toggleMember(userId)} className="rounded bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                        {memberName(userId)} ×
                      </button>
                    ))}
                    {formData.pendingEmails.map(email => (
                      <button
                        key={email}
                        type="button"
                        onClick={() => setFormData(current => ({ ...current, pendingEmails: current.pendingEmails.filter(item => item !== email) }))}
                        className="rounded bg-amber-50 px-2 py-0.5 text-[10px] text-amber-800"
                      >
                        {email} · convite pendente ×
                      </button>
                    ))}
                    {formData.legacyNames.map(name => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => setFormData(current => ({ ...current, legacyNames: current.legacyNames.filter(item => item !== name) }))}
                        className="rounded bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500"
                      >
                        {name} ×
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  <input
                    type="email"
                    value={inviteDraft}
                    onChange={event => setInviteDraft(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addInviteEmail();
                      }
                    }}
                    className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                    placeholder="E-mail de quem ainda não está na organização"
                  />
                  <button type="button" onClick={addInviteEmail} className="rounded border border-slate-200 px-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300">
                    Convidar
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Ganhos Esperados</label>
                <input
                  type="text"
                  value={formData.expectedGains}
                  onChange={event => setFormData(current => ({ ...current, expectedGains: event.target.value }))}
                  className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  placeholder="Ex: Redução de 30% no lead time"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="rounded px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!formData.title || !formData.plannedStart || !formData.plannedEnd}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {editingItem ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
